<!-- Page Header -->
<div class="d-flex justify-content-between align-items-start mb-4">
  <div>
    <nav aria-label="breadcrumb">
      <ol class="breadcrumb mb-1">
        <li class="breadcrumb-item"><a routerLink="/logbook/chemicals/custom">Custom Chemicals</a></li>
        <li class="breadcrumb-item active">View Chemical</li>
      </ol>
    </nav>
    <div class="d-flex align-items-center gap-3">
      <h1 class="h2 mb-0 fw-bold">{{ chemical?.name || 'Custom Chemical' }}</h1>
      <span *ngIf="chemical" class="badge" [ngClass]="getCategoryBadgeClass(chemical.category)">
        {{ getCategoryLabel(chemical.category) }}
      </span>
    </div>
  </div>
  <div class="d-flex gap-2">
    <button class="btn btn-outline-danger" (click)="confirmDelete()">
      <i class="bi bi-trash me-2"></i>Delete
    </button>
    <button class="btn btn-outline-primary" (click)="editChemical()">
      <i class="bi bi-pencil me-2"></i>Edit
    </button>
    <button class="btn btn-outline-secondary" (click)="goBack()">
      <i class="bi bi-arrow-left me-2"></i>Back
    </button>
  </div>
</div>

<!-- Loading State -->
<div *ngIf="isLoading" class="text-center py-5">
  <div class="spinner-border text-primary" role="status">
    <span class="visually-hidden">Loading...</span>
  </div>
  <p class="text-muted mt-2">Loading chemical details...</p>
</div>

<!-- Error State -->
<div *ngIf="!isLoading && errorMessage" class="alert alert-danger">
  <i class="bi bi-exclamation-triangle me-2"></i>{{ errorMessage }}
</div>

<!-- Main Content -->
<div *ngIf="!isLoading && chemical" class="row">
  <div class="col-lg-8">

    <!-- Section 1: Basic Information -->
    <div class="card mb-4">
      <div class="card-header p-3">
        <h5 class="mb-0"><i class="bi bi-info-circle me-2"></i>Basic Information</h5>
      </div>
      <div class="card-body">
        <div class="row g-3">
          <div class="col-md-12">
            <label class="form-label text-muted small mb-1">Chemical Name</label>
            <p class="mb-0 fw-medium fs-5">{{ chemical.name }}</p>
          </div>
          <div class="col-md-4">
            <label class="form-label text-muted small mb-1">Category</label>
            <p class="mb-0">
              <span class="badge" [ngClass]="getCategoryBadgeClass(chemical.category)">
                {{ getCategoryLabel(chemical.category) }}
              </span>
            </p>
          </div>
          <div class="col-md-4" *ngIf="chemical.manufacturer">
            <label class="form-label text-muted small mb-1">Manufacturer</label>
            <p class="mb-0">{{ chemical.manufacturer }}</p>
          </div>
          <div class="col-md-4" *ngIf="chemical.epaNumber">
            <label class="form-label text-muted small mb-1">EPA Number</label>
            <p class="mb-0"><code>{{ chemical.epaNumber }}</code></p>
          </div>
          <div class="col-md-6" *ngIf="chemical.activeIngredient">
            <label class="form-label text-muted small mb-1">Active Ingredient</label>
            <p class="mb-0">{{ chemical.activeIngredient }}</p>
          </div>
          <div class="col-md-6" *ngIf="chemical.formulation">
            <label class="form-label text-muted small mb-1">Formulation</label>
            <p class="mb-0">{{ chemical.formulation }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Section 2: Application Details -->
    <div class="card mb-4">
      <div class="card-header p-3">
        <h5 class="mb-0"><i class="bi bi-droplet me-2"></i>Application Details</h5>
      </div>
      <div class="card-body">
        <div class="row g-3">
          <div class="col-md-12">
            <label class="form-label text-muted small mb-1">Default Application Rate</label>
            <p class="mb-0 fw-medium fs-5 text-primary">{{ chemical.defaultRate || 'Not specified' }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Section 3: Safety Information -->
    <div class="card mb-4" *ngIf="chemical.signalWord || chemical.rei || chemical.phi">
      <div class="card-header p-3">
        <h5 class="mb-0"><i class="bi bi-shield-exclamation me-2"></i>Safety Information</h5>
      </div>
      <div class="card-body">
        <div class="row g-3">
          <div class="col-md-4" *ngIf="chemical.signalWord">
            <label class="form-label text-muted small mb-1">Signal Word</label>
            <p class="mb-0">
              <span class="badge" [ngClass]="chemical.signalWord === 'DANGER' ? 'bg-danger' : chemical.signalWord === 'WARNING' ? 'bg-warning' : 'bg-info'">
                {{ chemical.signalWord }}
              </span>
            </p>
          </div>
          <div class="col-md-4" *ngIf="chemical.rei">
            <label class="form-label text-muted small mb-1">REI (Restricted Entry Interval)</label>
            <p class="mb-0 fw-medium">{{ chemical.rei }}</p>
          </div>
          <div class="col-md-4" *ngIf="chemical.phi">
            <label class="form-label text-muted small mb-1">PHI (Pre-Harvest Interval)</label>
            <p class="mb-0 fw-medium">{{ chemical.phi }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Section 4: Target Pests -->
    <div class="card mb-4" *ngIf="chemical.targetPests && chemical.targetPests.length > 0">
      <div class="card-header p-3">
        <h5 class="mb-0"><i class="bi bi-bug me-2"></i>Target Pests</h5>
      </div>
      <div class="card-body">
        <div class="d-flex flex-wrap gap-2">
          <span *ngFor="let pest of chemical.targetPests" class="badge bg-secondary-subtle text-secondary">
            {{ pest }}
          </span>
        </div>
      </div>
    </div>

    <!-- Section 5: Target Crops -->
    <div class="card mb-4" *ngIf="chemical.crops && chemical.crops.length > 0">
      <div class="card-header p-3">
        <h5 class="mb-0"><i class="bi bi-flower1 me-2"></i>Target Crops</h5>
      </div>
      <div class="card-body">
        <div class="d-flex flex-wrap gap-2">
          <span *ngFor="let crop of chemical.crops" class="badge bg-primary-subtle text-primary">
            {{ crop }}
          </span>
        </div>
      </div>
    </div>

    <!-- Section 6: Additional Notes -->
    <div class="card mb-4" *ngIf="chemical.notes">
      <div class="card-header p-3">
        <h5 class="mb-0"><i class="bi bi-chat-left-text me-2"></i>Additional Notes</h5>
      </div>
      <div class="card-body">
        <p class="mb-0">{{ chemical.notes }}</p>
      </div>
    </div>

  </div>

  <!-- Right Sidebar -->
  <div class="col-lg-4">
    <div class="sticky-top sticky-sidebar">
    <!-- Quick Info Card -->
    <div class="card mb-4">
      <div class="card-header p-3 bg-primary-subtle">
        <h6 class="mb-0 text-primary"><i class="bi bi-info-circle me-2"></i>Quick Info</h6>
      </div>
      <div class="card-body">
        <div class="mb-3">
          <label class="form-label text-muted small mb-1">Chemical ID</label>
          <p class="mb-0 small"><code>{{ chemical.id }}</code></p>
        </div>
        <div class="mb-3" *ngIf="chemical.createdAt">
          <label class="form-label text-muted small mb-1">Created</label>
          <p class="mb-0 small">{{ chemical.createdAt | date:'short' }}</p>
        </div>
        <div class="mb-3" *ngIf="chemical.updatedAt">
          <label class="form-label text-muted small mb-1">Last Updated</label>
          <p class="mb-0 small">{{ chemical.updatedAt | date:'short' }}</p>
        </div>
        <div class="mb-0" *ngIf="chemical.crops && chemical.crops.length > 0">
          <label class="form-label text-muted small mb-1">Approved Crops</label>
          <p class="mb-0 fw-medium">{{ chemical.crops.length }} crop(s)</p>
        </div>
      </div>
    </div>
    </div>
  </div>
</div>

<!-- Confirm Delete Modal -->
<app-confirm-modal
  #confirmModal
  (confirmed)="onDeleteConfirmed()"
  (cancelled)="onDeleteCancelled()"
></app-confirm-modal>
